import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useQueryClient } from '@tanstack/react-query';
import { Space, Button, Typography } from 'antd';
import {
  ApplicationUIContext,
  Table,
  basicSort,
  dateSort,
  Drawer,
  useDrawerState,
} from '@yuhuinc/ui-library';
import { getTranslatedString } from 'helpers/i18n';
import { toISO8601DateString } from 'helpers/Formatter';
import { CardSectionTitle } from '../../common/Styles';
import { CurrentApplicationContext } from '../../ApplicationOverview';
import RunBackgroundCheck from './RunBackgroundCheck';
import { DeleteOutlined } from '@ant-design/icons';
import { phaseTwoWip } from '../../common/actions/WipModal';
import { featureEnabled } from 'helpers/ToggleBot';
import { canRunBackgroundChecksForApplication } from '../../common/helpers';

const { Link } = Typography;

const COLUMNS = [
  {
    title: getTranslatedString(
      'landlordPortal.applicationManagement.applicationOverview.applicants.backgroundChecks.check'
    ),
    dataIndex: 'name',
    key: 'name',
    width: '80%',
    sorter: (a, b) => basicSort(a.name, b.name),
    render(_, row) {
      const name = getTranslatedString(
        `landlordPortal.applicationManagement.applicationOverview.applicants.backgroundChecks.${row.check_report_type}`
      );

      return row.url ? <Link href={row.url}>{name}</Link> : name;
    },
  },
  {
    title: getTranslatedString(
      'landlordPortal.applicationManagement.applicationOverview.applicants.backgroundChecks.pulledAt'
    ),
    dataIndex: 'pulledAt',
    key: 'pulledAt',
    width: 100,
    sorter: dateSort,
    render(_, row) {
      return row.pulled_at ? toISO8601DateString(row.pulled_at) : '-';
    },
  },
  {
    key: 'actions',
    render() {
      return (
        <Button
          type="link"
          icon={<DeleteOutlined />}
          onClick={() => {
            phaseTwoWip(() => {});
          }}
        />
      );
    },
    fixed: 'right',
  },
];

const BackgroundChecks = ({ applicant }) => {
  const { isMobile } = useContext(ApplicationUIContext);
  const currentApplication = useContext(CurrentApplicationContext);
  const [, addBackgroundCheck] = useDrawerState(false);
  const queryClient = useQueryClient();

  const backgroundCheckRunning = queryClient.isMutating({ mutationKey: ['run_background_check'] });
  const canRunBackgroundChecks = canRunBackgroundChecksForApplication(
    currentApplication,
    applicant
  );
  const backgroundCheckReports = applicant.background_check_reports;

  const onRunBackgroundCheckSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['applications'] });
    addBackgroundCheck.close();
  };

  return (
    <>
      <Space direction="vertical" size="middle">
        <CardSectionTitle>
          {getTranslatedString(
            'landlordPortal.applicationManagement.applicationOverview.applicants.backgroundChecks'
          )}
        </CardSectionTitle>
        {!featureEnabled('4-0-phase-2-wip') && backgroundCheckReports.length > 0 && (
          <Table
            dataSource={backgroundCheckReports}
            columns={COLUMNS}
            hideActionAndSearch
            size={isMobile ? 'middle' : 'small'}
            scroll={{
              x: isMobile,
            }}
            pagination={{ hideOnSinglePage: true }}
            rowKey="id"
          />
        )}
        <Button
          size={isMobile && 'large'}
          onClick={addBackgroundCheck.open}
          disabled={!canRunBackgroundChecks || backgroundCheckRunning}
          loading={backgroundCheckRunning}
        >
          {getTranslatedString(
            'landlordPortal.applicationManagement.applicationOverview.applicants.backgroundChecks.addACheck'
          )}
        </Button>
      </Space>
      <Drawer
        title={getTranslatedString(
          'landlordPortal.applicationManagement.applicationOverview.applicants.backgroundChecks.drawerTitle'
        )}
        visible={addBackgroundCheck.isVisible}
        closable
        destroyOnClose
        onClose={addBackgroundCheck.close}
        width={Drawer.DEFAULT_SIZE}
      >
        <RunBackgroundCheck
          onSuccess={onRunBackgroundCheckSuccess}
          applicants={currentApplication.cotenants.filter(
            (cotenant) => cotenant.lifecycle?.status === 'submitted'
          )}
          creditCheckOptions={currentApplication.application_permissions}
        />
      </Drawer>
    </>
  );
};

BackgroundChecks.propTypes = {
  applicant: PropTypes.object.isRequired,
};

export default BackgroundChecks;
